import React, { useState } from 'react';
import { Container } from 'react-bootstrap';
import { Search, Linkedin, UserSearch, SlidersHorizontal } from 'lucide-react';
import Avatar from './Avatar';
import { Loader, EmptyState, ViewToggle, trackPointer } from './UI';
import { DEFAULT_BIO, truncateText, getValidUrl } from '../utils/Utils';

const CARD_BIO_MAX_LENGTH = 90;
const hasCustomBio = (user) => user.bio && user.bio.trim() !== '' && user.bio.trim() !== DEFAULT_BIO;

/** Categorías del miembro, recortadas para no saturar la tarjeta. */
function CategoryChips({ categories = [], max = 3, align = 'center' }) {
  if (categories.length === 0) return null;

  const shown = categories.slice(0, max);
  const hidden = categories.length - shown.length;

  return (
    <div className="dyd-cat-chips" style={{ justifyContent: align }}>
      {shown.map((category) => <span key={category} className="dyd-chip">{category}</span>)}
      {hidden > 0 && <span className="dyd-chip" title={categories.slice(max).join(', ')}>+{hidden}</span>}
    </div>
  );
}

function UsersComponent({ Users, categorias, loading }) {
  const [selectedCategory, setSelectedCategory] = useState("Todos");
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("grid");

  const filteredUsers = Users.filter(user => {
    const categoryMatch = selectedCategory === "Todos" ||
      user.networkingCategories?.includes(selectedCategory);
    const searchMatch = searchTerm === "" ||
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.surname.toLowerCase().includes(searchTerm.toLowerCase());
    return categoryMatch && searchMatch;
  });


  const hasFilters = searchTerm !== "" || selectedCategory !== "Todos";

  return (
    <div className="dyd-page">
      <Container>
        <div className="dyd-page-head">
          <div>
            <span className="dyd-eyebrow">Networking</span>
            <h1 style={{ marginTop: 16, marginBottom: 10 }}>
              La comunidad <span className="dyd-gradient-text">al completo</span>
            </h1>
          </div>
          {!loading && (
            <span className="dyd-chip">
              {filteredUsers.length} {filteredUsers.length === 1 ? 'miembro' : 'miembros'}
            </span>
          )}
        </div>

        <p className="dyd-lead" style={{ marginBottom: 28 }}>
          Aquí se reúnen todos los miembros del club. Explora y descubre el talento detrás de cada uno.
        </p>

        {/* Filtros */}
        <div className="dyd-panel dyd-filters">
          <div className="dyd-field" style={{ flex: '1 1 260px' }}>
            <label className="dyd-label" htmlFor="userSearch">
              <Search size={12} style={{ marginRight: 6, verticalAlign: '-1px' }} />
              Buscar
            </label>
            <input
              id="userSearch"
              className="dyd-input"
              type="text"
              placeholder="Nombre o apellido…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="dyd-field" style={{ flex: '1 1 260px' }}>
            <label className="dyd-label" htmlFor="userCategory">
              <SlidersHorizontal size={12} style={{ marginRight: 6, verticalAlign: '-1px' }} />
              Categoría
            </label>
            <select
              id="userCategory"
              className="dyd-select"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <option value="Todos">Todas las categorías</option>
              {categorias.map((cat, index) => <option key={index} value={cat}>{cat}</option>)}
            </select>
          </div>

          <ViewToggle value={viewMode} onChange={setViewMode} />
        </div>

        {loading ? (
          <Loader text="Cargando miembros" />
        ) : filteredUsers.length === 0 ? (
          <EmptyState
            icon={UserSearch}
            title={hasFilters ? "Ningún miembro coincide con los filtros" : "Aún no hay miembros en esta categoría"}
            hint={hasFilters ? "Prueba a ampliar la búsqueda o cambiar de categoría." : undefined}
          />
        ) : viewMode === "grid" ? (
          <div className="dyd-grid">
            {filteredUsers.map((user, index) => (
              <article
                key={user._id || index}
                className="dyd-card dyd-rise dyd-user-card"
                style={{ '--i': index % 12, cursor: 'pointer' }}
                onMouseMove={trackPointer}
                onClick={() => (window.location.href = `/profile/${user._id}`)}
              >
                <div
                  className={`dyd-user-head ${user.coverPicture ? "has-cover" : ""}`}
                  style={user.coverPicture ? { backgroundImage: `url(${user.coverPicture})` } : undefined}
                >
                  <Avatar
                    src={user.profilePicture}
                    name={user.name}
                    surname={user.surname}
                    size={104}
                    ring
                  />
                </div>

                <div className="dyd-card-body" style={{ alignItems: 'center', textAlign: 'center' }}>
                  <h3 className="dyd-card-title">{user.name} {user.surname}</h3>
                  <CategoryChips categories={user.networkingCategories} />
                  {hasCustomBio(user) && (
                    <p className="dyd-item-text dyd-bio-clamp">{truncateText(user.bio, CARD_BIO_MAX_LENGTH)}</p>
                  )}

                  <div className="dyd-card-actions" style={{ width: '100%' }}>
                    <a className="dyd-btn dyd-btn-primary dyd-btn-block" href={`/profile/${user._id}`}>
                      Visitar perfil
                    </a>
                    {user.linkedIn && (
                      <a
                        className="dyd-btn dyd-btn-ghost dyd-btn-block"
                        href={getValidUrl(user.linkedIn)}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Linkedin size={16} />
                        LinkedIn
                      </a>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="dyd-list">
            {filteredUsers.map((user, index) => (
              <div
                key={user._id || index}
                className="dyd-list-item dyd-rise"
                style={{ '--i': index % 12, cursor: 'pointer' }}
                onClick={() => (window.location.href = `/profile/${user._id}`)}
              >
                <Avatar
                  src={user.profilePicture}
                  name={user.name}
                  surname={user.surname}
                  size={56}
                  ring
                />

                <div className="dyd-list-body">
                  <h3 className="dyd-list-title">{user.name} {user.surname}</h3>
                  <CategoryChips categories={user.networkingCategories} align="flex-start" />
                  {hasCustomBio(user) && (
                    <p className="dyd-list-text dyd-bio-clamp">{truncateText(user.bio, CARD_BIO_MAX_LENGTH)}</p>
                  )}
                </div>

                <div className="dyd-list-actions">
                  <a className="dyd-btn dyd-btn-primary" href={`/profile/${user._id}`}>
                    Visitar perfil
                  </a>
                  {user.linkedIn && (
                    <a
                      className="dyd-btn dyd-btn-ghost"
                      href={getValidUrl(user.linkedIn)}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="LinkedIn"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Linkedin size={16} />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Container>

      <style>{`
        .dyd-user-head {
          position: relative;
          display: flex;
          justify-content: center;
          padding: 30px 0 4px;
          background: radial-gradient(60% 100% at 50% 0%, rgba(59,130,246,0.14), transparent 70%);
        }
        /* Con portada, la imagen ocupa la franja de arriba y el avatar la pisa */
        .dyd-user-head.has-cover {
          background-size: cover;
          background-position: center;
          padding-top: 74px;
        }
        .dyd-user-head.has-cover::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(180deg, rgba(5,8,14,0.25), rgba(5,8,14,0.82));
        }
        .dyd-user-head.has-cover .dyd-avatar-ring { position: relative; }
        .dyd-user-card:hover .dyd-avatar-ring { transform: scale(1.05); }
        .dyd-bio-clamp {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          min-height: 2.5em;
          margin: 0;
        }
      `}</style>
    </div>
  );
}

export default UsersComponent;
